/**
 * useTutorLiveFeedProposals — « <champion> propose » : une activité déjà générée, que le tuteur voit au centre ou envoie
 * d'un geste.
 *
 * **Une remédiation, et rien d'autre** (Wajih, live du 04/11) : un rendu vu dans la séance (`handIns`, le signal
 * `exercise_done`) fait relire sa correction (`pollActivityCorrection`) jusqu'à ce qu'elle soit finie ; beaucoup
 * d'erreurs (`needsRemediation`), et le champion prépare une « similaire » plus facile, ciblée sur ce qui est faux
 * (`generateRemediationSimilar`, consigne `remediationInstruction`). Sinon, rien.
 *
 * **Elle vit jusqu'à ce que le tuteur l'envoie ou l'écarte** : le serveur n'écrit la correction qu'une fois le rendu
 * validé, elle ne peut donc pas dépendre du rendu en attente. **Ce qu'elle est devenue est gardé**
 * (`tutorEventsPanel.helperProposals`) : un rechargement ne régénère rien et ne fait revenir ni une envoyée ni une
 * écartée.
 *
 * **Bornée** (`proposalAllowed`) : une à la fois par élève, une par élève toutes les 10 minutes (retenue, elle repart à
 * la levée), jamais pendant que le tuteur génère. **Mesurée** : `traceHelperProposal`.
 *
 * « Ajouter et montrer » crée l'activité et la montre (`createActivityAsTutor`, puis `newTodo` avec `forceFocus`), une
 * fois qu'aucune création du champ n'est en vol. La proposition en attente ressort en tête du champ (`pendingFor`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import {
    HELPER_PROPOSAL_RESUMABLE_MS,
    recordHelperProposal,
} from "../../../../../../data/redux/reducers/data/tutorEventsPanel"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { apiCall, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { traceHelperProposal } from "../../../../../../engine/live/tutor/helperProposalTraces"
import { type TutorReviewStageProps } from "../../../../../../design-system/organisms/TutorReviewStage"
import { type UIGeneratedActivity } from "../../../types"
import {
    createResultBody,
    toUIGeneratedActivity,
    type ApiGeneratedMaterial,
} from "../../../../adapters/parent/Activities/activityGenerationAdapter"
import {
    toUICorrectionPoints,
    type ApiCorrectionPoint,
} from "../../../../adapters/parent/Activities/activityDetailsAdapter"
import { type ApiStudentLists } from "../../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import {
    needsRemediation,
    PROPOSAL_COOLDOWN_MS,
    proposalAllowed,
    remediationCount,
    remediationInstruction,
    type TutorCorrectionPoint,
} from "../../../../view-models/tutor/Live/tutorLiveGenerationRules"
import { type TutorLiveTransientEntry } from "../../../../view-models/tutor/Live/tutorLiveFeedViewModel"
import { rawId } from "./tutorLiveContract"

const TEXTS = {
    reason: (firstname: string, title: string | null, count: string) =>
        `${firstname} a rendu ${title != null ? `« ${title} »` : "son exercice"} : ${count}. Une activité plus facile, sur ce qui est faux, est prête.`,
    short: (count: string, title: string | null) => (title != null ? `${count} à « ${title} »` : count),
    meta: "Prête · plus facile, ciblée sur ses erreurs",
    send: "Ajouter et montrer",
    sent: (firstname: string) => `Activité montrée à ${firstname}`,
    failed: "L'envoi n'a pas abouti. La proposition reste là.",
    wait: "Une activité part déjà vers l'élève : réessaie dans un instant.",
    heading: "La proposition du champion",
}

/** Une correction se relit toutes les 15 s, 20 fois au plus (5 minutes) : au-delà, le champion ne propose rien. */
export const CORRECTION_POLL_MS = 15_000
const CORRECTION_POLL_MAX = 20

/** Les états d'une correction que la relecture attend encore. */
const CORRECTION_WAITING = new Set(["", "pending", "generating"])

type Proposal = {
    key: string
    studentId: string
    activityId: string
    startedAt: number
    /**
     * `reading` : la correction se relit ; `corrected` : beaucoup d'erreurs, la génération attend son tour ;
     * `pending` : elle s'écrit ; `ready`, `error` ; `none` : rien à proposer (peu d'erreurs, pas de correction).
     */
    status: "reading" | "corrected" | "pending" | "ready" | "error" | "none"
    /** Le titre de l'exercice rendu, que la carte cite (pas celui de la proposition). */
    sourceTitle: string | null
    materialId?: string | number
    level?: number | null
    duration?: number | null
    points?: TutorCorrectionPoint[]
    material?: ApiGeneratedMaterial
    activity?: UIGeneratedActivity
    send: "idle" | "sending" | "sent"
    dismissed?: boolean
}

/** Ce qu'une proposition est devenue, gardé d'un rechargement à l'autre (`tutorEventsPanel.helperProposals`). */
export type TutorLiveHelperProposalMemory = {
    at?: number
    outcome?: "requested" | "ready" | "sending" | "sent" | "dismissed" | "none"
    /** Le titre de l'exercice rendu, que la carte cite. */
    sourceTitle?: string | null
    points?: TutorCorrectionPoint[]
    material?: ApiGeneratedMaterial
}

/** La proposition en attente d'un élève, telle que le champ la montre en tête. */
export type TutorLivePendingProposal = {
    entryId: string
    title: string
    reason: string
    sendLabel: string
    sending: boolean
}

export type TutorLiveFeedProposalsDeps = {
    /** Les rendus vus dans la séance (le signal `exercise_done`, validés ou non) : chacun fait relire sa correction. */
    handIns: { studentId: string; activityId: string }[]
    /** Ce que les propositions sont devenues avant un rechargement, par rendu (`studentId:activityId`). */
    memory?: Record<string, TutorLiveHelperProposalMemory | undefined>
    accountStudents: (ApiStudentLists & { id?: string | number })[]
    firstnameOf: (studentId: string) => string
    send:
        ((type: string, data: Record<string, unknown>, to: number[] | null, policy: "keep" | "drop") => unknown) | null
    telemetry: EngineTelemetry
    /** Le tuteur génère dans le champ : aucune proposition ne part d'avance. */
    tutorGenerating: boolean
    /** Une création du champ attend sa réponse (lue au geste) : « Envoyer » attend (la même tranche), et le dit. */
    tutorCreating: () => boolean
    /** Une présentation tourne : la relecture se pose sur ce que voient les élèves, elle le dit. */
    presenting: boolean
    quizAvailable: boolean
    notify: (message: string, tone: "success" | "danger" | "info") => void
    helperName: string
}

export type TutorLiveFeedProposals = {
    /** Les cartes « <champion> propose » du fil, chacune pour son élève. */
    transient: TutorLiveTransientEntry[]
    /** « Voir », « Envoyer », « Écarter » d'une carte (son id d'entrée). */
    onAction: (entryId: string, action: "see" | "send" | "dismiss") => boolean
    /** La proposition vue au centre (« Voir »), ou `null`. */
    review: TutorReviewStageProps | null
    /** Une proposition part (sa création attend sa réponse) : le champ attend pour créer les siennes. */
    sending: boolean
    /** La proposition d'un élève que le tuteur n'a ni envoyée ni écartée, la plus récente ; `null` sinon. */
    pendingFor: (studentId: string) => TutorLivePendingProposal | null
}

const ENTRY_PREFIX = "proposal-"

type RawActivity = {
    id?: string | number
    estimated_duration?: number | null
    material?: { id?: string | number | null; title?: string | null; level?: string | number | null } | null
}
type CorrectedActivity = { correction_status?: string | null; correction_points?: ApiCorrectionPoint[] | null }

/** Le type de l'exercice rendu (1 à 3) et sa durée (5, 10, 15) : ce que l'endpoint accepte, sinon rien. */
const LEVELS = new Set([1, 2, 3])
const DURATIONS = new Set([5, 10, 15])
function remediationBody(proposal: Proposal): Record<string, unknown> {
    const body: Record<string, unknown> = {
        material_id: proposal.materialId,
        student_ids: [rawId(proposal.studentId)],
        instructions: remediationInstruction(proposal.points ?? []),
    }
    if (proposal.level != null && LEVELS.has(proposal.level)) body.target_level = proposal.level
    if (proposal.duration != null && DURATIONS.has(proposal.duration)) body.duration = proposal.duration
    return body
}

/** Une activité de l'élève, lue dans ses listes (faites et à faire). */
function rawActivity(
    students: TutorLiveFeedProposalsDeps["accountStudents"],
    studentId: string,
    activityId: string
): RawActivity | undefined {
    const student = students.find((candidate) => String(candidate?.id) === studentId)
    return [...(student?.activities ?? []), ...(student?.todo_activities ?? [])].find(
        (activity) => String(activity.id) === activityId
    ) as RawActivity | undefined
}

const toInt = (value: unknown): number | null => {
    const parsed = parseInt(String(value ?? ""), 10)
    return Number.isNaN(parsed) ? null : parsed
}

/**
 * La proposition d'un rendu reprise après un rechargement : envoyée, écartée ou sans objet, elle ne revient pas ;
 * prête, elle revient telle quelle, sans nouvel appel ; demandée sans réponse, elle repart (sa borne comptée).
 */
function resume(base: Proposal, memory: TutorLiveHelperProposalMemory | undefined): Proposal {
    switch (memory?.outcome) {
        case "sent":
        case "sending":
        case "dismissed":
        case "none":
            return { ...base, status: "none" }
        case "ready":
            if (memory.material == null) return { ...base, status: "none" }
            return {
                ...base,
                status: "ready",
                startedAt: memory.at ?? base.startedAt,
                points: memory.points,
                material: memory.material,
                activity: { ...toUIGeneratedActivity(memory.material), id: base.key, status: "ready" },
            }
        case "requested":
            return { ...base, status: "corrected", points: memory.points ?? [] }
        default:
            return base
    }
}

export function useTutorLiveFeedProposals(deps: TutorLiveFeedProposalsDeps): TutorLiveFeedProposals {
    const dispatch = useDispatch()
    const depsRef = useRef(deps)
    depsRef.current = deps
    const [proposals, setProposals] = useState<Record<string, Proposal>>({})
    const proposalsRef = useRef(proposals)
    proposalsRef.current = proposals
    const lastStartedAt = useRef<Record<string, number>>({})
    const requested = useRef(0)
    const [seeing, setSeeing] = useState<string | null>(null)
    // Les relectures en cours et la levée de la borne : arrêtées au démontage ; une réponse arrivée après ne relance rien.
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
    const unmounted = useRef(false)
    useEffect(() => {
        unmounted.current = false
        const pending = timers.current
        return () => {
            unmounted.current = true
            for (const timer of Object.values(pending)) clearTimeout(timer)
        }
    }, [])
    // Relance la génération quand la borne d'un élève se lève.
    const [cooldownTick, setCooldownTick] = useState(0)

    const submissions = deps.handIns
    const submissionsKey = submissions.map((s) => `${s.studentId}:${s.activityId}`).join("|")

    const record = useCallback(
        (key: string, patch: TutorLiveHelperProposalMemory) => {
            dispatch(recordHelperProposal(key, patch) as never)
        },
        [dispatch]
    )

    const update = useCallback((key: string, patch: Partial<Proposal>) => {
        proposalsRef.current =
            proposalsRef.current[key] != null
                ? { ...proposalsRef.current, [key]: { ...proposalsRef.current[key], ...patch } }
                : proposalsRef.current
        setProposals(proposalsRef.current)
    }, [])

    // Une proposition naît d'un rendu (ou d'un rechargement) : reprise où elle en était, la borne de son élève comptée
    // pour une génération aboutie (une génération perdue au rechargement repart tout de suite).
    const bear = useCallback(
        (key: string, raw: RawActivity | undefined, memory: TutorLiveHelperProposalMemory | undefined): Proposal => {
            const [studentId, activityId] = key.split(":")
            const born = resume(
                {
                    key,
                    studentId,
                    activityId,
                    startedAt: memory?.at ?? Date.now(),
                    status: "reading",
                    sourceTitle: memory?.sourceTitle ?? raw?.material?.title ?? null,
                    materialId: raw?.material?.id ?? undefined,
                    level: toInt(raw?.material?.level),
                    duration: toInt(raw?.estimated_duration),
                    send: "idle",
                },
                memory
            )
            if (memory?.at != null && memory.outcome !== "requested")
                lastStartedAt.current[studentId] = Math.max(lastStartedAt.current[studentId] ?? 0, memory.at)
            proposalsRef.current = { ...proposalsRef.current, [key]: born }
            setProposals(proposalsRef.current)
            return born
        },
        []
    )

    // ── La correction du rendu, relue jusqu'à ce qu'elle soit finie.
    const readActivity = useCallback(
        (activityId: string, withLogs: boolean) =>
            apiCall<CorrectedActivity[] | { data?: CorrectedActivity[] }>(
                dispatch as never,
                activitiesService.pollActivityCorrection as ApiService,
                {
                    urlParams: {
                        activity_ids: activityId,
                        include_archived: 0,
                        ...(withLogs && { with_logs: 1 }),
                    },
                }
            ).then((response) => (Array.isArray(response) ? response : (response?.data ?? []))[0] ?? null),
        [dispatch]
    )
    const readCorrection = useCallback(
        (key: string, attempt: number) => {
            const proposal = proposalsRef.current[key]
            if (unmounted.current || proposal == null || proposal.status !== "reading") return
            // L'attente lit le seul statut ; les points (et les journaux qui les portent) ne se lisent qu'une fois.
            readActivity(proposal.activityId, false)
                .then(async (waiting) => {
                    if (unmounted.current) return
                    const status = waiting?.correction_status ?? ""
                    if (status === "completed" && waiting?.correction_points == null)
                        return readActivity(proposal.activityId, true)
                    return waiting
                })
                .then((activity) => {
                    if (unmounted.current || activity === undefined) return
                    const status = activity?.correction_status ?? ""
                    if (CORRECTION_WAITING.has(status)) {
                        if (attempt + 1 >= CORRECTION_POLL_MAX) {
                            update(key, { status: "none" })
                            record(key, { outcome: "none" })
                        } else
                            timers.current[key] = setTimeout(() => readCorrection(key, attempt + 1), CORRECTION_POLL_MS)
                        return
                    }
                    // La cible de la consigne est ce que la correction a écrit de l'erreur (la compétence rattachée,
                    // sinon sa notion) : l'écran tait une notion non rattachée (« Notion à préciser »), la consigne d'une
                    // remédiation en a besoin. Une erreur sans rien d'écrit compte, sans cible.
                    const raw = activity?.correction_points ?? []
                    const byIndex = new Map(raw.map((point) => [point?.index, point]))
                    const points = toUICorrectionPoints(raw).map((point) => {
                        const source = byIndex.get(point.index)
                        return { type: point.type, notion: (source?.skill_part_name || source?.notion || "").trim() }
                    })
                    if (needsRemediation(points)) update(key, { status: "corrected", points })
                    else {
                        update(key, { status: "none" })
                        record(key, { outcome: "none" })
                    }
                })
                .catch(() => update(key, { status: "none" }))
        },
        [readActivity, update, record]
    )

    useEffect(() => {
        const d = depsRef.current
        for (const submission of submissions) {
            const key = `${submission.studentId}:${submission.activityId}`
            if (proposalsRef.current[key] != null) continue
            const raw = rawActivity(d.accountStudents, submission.studentId, submission.activityId)
            if (raw?.material?.id == null) continue
            const born = bear(key, raw, d.memory?.[key])
            if (born.status === "reading") readCorrection(key, 0)
        }
        // `submissionsKey` dit quand les rendus changent ; le reste se lit par refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionsKey, readCorrection])

    // Une proposition prête dont le rendu n'est plus signalé (validé par le tuteur, ou un autre rendu depuis) revient
    // aussi d'un rechargement, tant qu'elle n'est ni envoyée ni écartée : pour un élève de la séance, des 3 dernières heures.
    useEffect(() => {
        const d = depsRef.current
        const now = Date.now()
        const inRoom = new Set(d.accountStudents.map((student) => String(student?.id)))
        for (const [key, memory] of Object.entries(d.memory ?? {})) {
            if (memory?.outcome !== "ready" || memory.material == null || proposalsRef.current[key] != null) continue
            const [studentId, activityId] = key.split(":")
            if (!inRoom.has(studentId) || memory.at == null || now - memory.at > HELPER_PROPOSAL_RESUMABLE_MS) continue
            bear(key, rawActivity(d.accountStudents, studentId, activityId), memory)
        }
        // Une fois, à l'arrivée dans la salle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ── Beaucoup d'erreurs : la « similaire » de remédiation part, quand la borne le permet.
    const correctedKey = Object.values(proposals)
        .filter((proposal) => proposal.status === "corrected")
        .map((proposal) => proposal.key)
        .join("|")
    useEffect(() => {
        const d = depsRef.current
        let nextRelease: number | null = null
        for (const proposal of Object.values(proposalsRef.current)) {
            if (proposal.status !== "corrected") continue
            const now = Date.now()
            const inFlight = new Set(
                Object.values(proposalsRef.current)
                    .filter((other) => other.status === "pending")
                    .map((other) => other.studentId)
            )
            if (
                !proposalAllowed({
                    studentId: proposal.studentId,
                    now,
                    inFlight,
                    lastStartedAt: lastStartedAt.current,
                    tutorGenerating: d.tutorGenerating,
                })
            ) {
                // Retenue par la borne des 10 minutes : elle repart d'elle-même à sa levée.
                const last = lastStartedAt.current[proposal.studentId]
                if (last != null && now - last < PROPOSAL_COOLDOWN_MS)
                    nextRelease = Math.min(nextRelease ?? Infinity, last + PROPOSAL_COOLDOWN_MS)
                continue
            }
            lastStartedAt.current[proposal.studentId] = now
            requested.current += 1
            const key = proposal.key
            update(key, { status: "pending", startedAt: now })
            record(key, {
                at: now,
                outcome: "requested",
                sourceTitle: proposal.sourceTitle,
                points: proposal.points,
            })
            traceHelperProposal(d.telemetry, "requested", { count: requested.current, signal: "correction_errors" })
            apiCall<ApiGeneratedMaterial | { data?: ApiGeneratedMaterial }>(
                dispatch as never,
                activitiesService.generateRemediationSimilar as ApiService,
                { data: remediationBody(proposal) }
            )
                .then((response) => {
                    const material = ((response as { data?: ApiGeneratedMaterial })?.data ??
                        response) as ApiGeneratedMaterial
                    record(key, { outcome: "ready", material })
                    if (unmounted.current) return
                    update(key, {
                        status: "ready",
                        material,
                        activity: { ...toUIGeneratedActivity(material), id: key, status: "ready" },
                    })
                    traceHelperProposal(depsRef.current.telemetry, "ready", { durationMs: Date.now() - now })
                })
                .catch(() => {
                    if (unmounted.current) return
                    update(key, { status: "error" })
                    traceHelperProposal(depsRef.current.telemetry, "failed", { durationMs: Date.now() - now })
                })
        }
        if (nextRelease == null) return undefined
        const timer = setTimeout(() => setCooldownTick((tick) => tick + 1), Math.max(0, nextRelease - Date.now()))
        timers.current.cooldown = timer
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [correctedKey, submissionsKey, deps.tutorGenerating, cooldownTick, dispatch, update, record])

    // Une proposition vit jusqu'à ce que le tuteur l'envoie ou l'écarte ; envoyée, elle reste une fois (son accusé).
    const shown = Object.values(proposals).filter(
        (proposal) => proposal.dismissed !== true && proposal.status === "ready"
    )

    const reasonOf = (proposal: Proposal) =>
        TEXTS.reason(
            depsRef.current.firstnameOf(proposal.studentId),
            proposal.sourceTitle,
            remediationCount(proposal.points ?? [])
        )

    const transient = useMemo<TutorLiveTransientEntry[]>(
        () =>
            shown.map((proposal) => {
                const iso = new Date(proposal.startedAt).toISOString()
                return {
                    studentId: proposal.studentId,
                    at: proposal.startedAt,
                    entry: {
                        kind: "proposal",
                        id: `${ENTRY_PREFIX}${proposal.key}`,
                        at: iso,
                        reason: reasonOf(proposal),
                        title: proposal.activity?.title ?? "",
                        meta: TEXTS.meta,
                        state: proposal.send === "idle" ? "ready" : proposal.send,
                        sendLabel: TEXTS.send,
                    },
                }
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shown.map((p) => `${p.key}:${p.send}:${p.status}`).join("|"), deps.firstnameOf]
    )

    const sendProposal = useCallback(
        (key: string) => {
            const d = depsRef.current
            const proposal = proposalsRef.current[key]
            if (proposal?.material == null || proposal.send !== "idle") return
            // Une création du champ attend sa réponse sur la même tranche : la proposition attend son tour, et le dit.
            if (d.tutorCreating()) {
                d.notify(TEXTS.wait, "info")
                return
            }
            update(key, { send: "sending" })
            // Un rechargement pendant l'envoi ne la refait pas partir : elle a peut-être été créée.
            record(key, { outcome: "sending" })
            const firstname = d.firstnameOf(proposal.studentId)
            apiCall<{ id?: string | number }>(dispatch as never, activitiesService.createActivityAsTutor, {
                routeParams: { id: proposal.studentId },
                data: {
                    ...createResultBody(proposal.material),
                    ...(d.quizAvailable ? { has_prerequisite_quiz: true } : {}),
                },
            })
                .then((activity) => {
                    const to = [Number(proposal.studentId)]
                    const id = rawId(proposal.studentId)
                    // Comme « Montrer à l'élève » : l'élève est prévenu, puis l'activité lui est montrée.
                    d.send?.("newTodo", { id, activity }, to, "drop")
                    d.send?.("newTodo", { id, activity, forceFocus: true }, to, "drop")
                    update(key, { send: "sent" })
                    record(key, { outcome: "sent" })
                    d.notify(TEXTS.sent(firstname), "success")
                    traceHelperProposal(d.telemetry, "sent", {})
                    setSeeing((current) => (current === key ? null : current))
                })
                .catch(() => {
                    record(key, { outcome: "ready", material: proposal.material })
                    update(key, { send: "idle" })
                    d.notify(TEXTS.failed, "danger")
                })
        },
        [dispatch, update, record]
    )

    const onAction = useCallback(
        (entryId: string, action: "see" | "send" | "dismiss") => {
            if (!entryId.startsWith(ENTRY_PREFIX)) return false
            const key = entryId.slice(ENTRY_PREFIX.length)
            if (action === "see") setSeeing(key)
            if (action === "send") sendProposal(key)
            if (action === "dismiss") {
                update(key, { dismissed: true })
                record(key, { outcome: "dismissed" })
                traceHelperProposal(depsRef.current.telemetry, "dismissed", {})
            }
            return true
        },
        [sendProposal, update, record]
    )

    const pendingFor = (studentId: string): TutorLivePendingProposal | null => {
        const latest = shown
            .filter((proposal) => proposal.studentId === studentId && proposal.send !== "sent")
            .sort((a, b) => b.startedAt - a.startedAt)[0]
        if (latest == null) return null
        return {
            entryId: `${ENTRY_PREFIX}${latest.key}`,
            title: latest.activity?.title ?? "",
            reason: TEXTS.short(remediationCount(latest.points ?? []), latest.sourceTitle),
            sendLabel: TEXTS.send,
            sending: latest.send === "sending",
        }
    }

    const seen = seeing != null ? proposals[seeing] : undefined
    const review: TutorReviewStageProps | null =
        seen?.activity != null
            ? {
                  open: true,
                  studentName: deps.firstnameOf(seen.studentId),
                  heading: TEXTS.heading,
                  proposals: [
                      {
                          ...seen.activity,
                          creation: seen.send === "sent" ? "created" : seen.send === "sending" ? "creating" : undefined,
                      },
                  ],
                  index: 0,
                  // Ni « Ajouter à la liste » ni « Régénérer » : la proposition est faite pour être montrée.
                  onSend: () => sendProposal(seen.key),
                  onClose: () => setSeeing(null),
                  sentId: seen.send === "sent" ? seen.key : null,
                  writingLabel: deps.helperName,
                  presenting: deps.presenting,
              }
            : null

    const sending = Object.values(proposals).some((proposal) => proposal.send === "sending")
    return { transient, onAction, review, sending, pendingFor }
}
